import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { updateDoc,doc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast, ToastContainer } from 'react-toastify';
import TechDetails from './JobUpdatePartials/TechDetails';
import PartsAndServices from './JobUpdatePartials/PartsAndServices';
import JobHeader from './JobUpdatePartials/JobHeader';
import StatusBar from './JobUpdatePartials/StatusBar';
import AssignTask from './JobUpdatePartials/AssignTask';
import JobCardInvoice from './JobUpdatePartials/JobCardInvoice';
import BreadCrumb from '../BreadCrumb';
import PhotoUploader from '../Photos/PhotoUploader';
import PhotoGallery from '../Photos/PhotoGallery';
import { usePhotos } from '../../hooks/usePhotos';
import { Spinner, Card, Badge } from '../UI';

const JobUpdate = () => {
    const { jobCardNo } = useParams();
    const [jobCard, setJobCard] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [activeComponent, setActiveComponent] = useState('techdetails');
    const [loading, setLoading] = useState(true);
    const { photos, loading: photosLoading, addPhotos, removePhoto } = usePhotos('job', jobCard?.id);

    const pages = [
        { name: 'Service Advisor', to: '/vehicles', component: Link, current: false },
        { name: 'Job Cards', to: '/vehicles/openjobs', component: Link, current: false },
        { name: 'Job Details', to: `/vehicles/jobDetails/${jobCardNo}`, component: Link, current: true },
    ];

 
    useEffect(() => {
        const fetchJobCard = async () => {
            try {
                const jobQuery = query(collection(db, 'jobs'), where('jobCardNo', '==', jobCardNo));
                const jobSnapshot = await getDocs(jobQuery);
                if (!jobSnapshot.empty) {
                    const jobData = jobSnapshot.docs[0].data();
                    const jobId = jobSnapshot.docs[0].id; // Save the document ID
                    setJobCard({ id: jobId, ...jobData }); // Store both ID and data
                } else {
                    toast.error('Job card not found');
                }
            } catch (error) {
                toast.error('Failed to fetch job card');
            } finally {
                setLoading(false);
            }
        };
        

        // Fetch the list of employees (mechanics)
        const fetchEmployees = async () => {
            try {
                const employeeQuery = query(collection(db, 'employees'), where('role', '==', 'Mechanic'));
                const employeeSnapshot = await getDocs(employeeQuery);
                setEmployees(employeeSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
            } catch (error) {
                toast.error('Failed to fetch employees');
            }
        };

        fetchJobCard();
        fetchEmployees();
    }, [jobCardNo]);


    const updateJobCard = async (updatedData) => {
        try {
          await updateDoc(doc(db, 'jobs', jobCard.id), updatedData);
          setJobCard(updatedData); // update state with new data
          toast.success("Job card updated successfully");
        } catch (error) {
          toast.error("Failed to update job card");
        }
      };


      const handleAddPart = async (newPart) => {
        const updatedParts = [...(jobCard.partsRequired || []), newPart];
    
        const updatedJobCard = {
            ...jobCard,
            partsRequired: updatedParts
        };
    
        await updateJobCard(updatedJobCard); 
    };
    
    const handleAddService = async (newService) => {
        const updatedServices = [...(jobCard.servicesRequired || []), newService];

        const updatedJobCard = {
            ...jobCard,
            servicesRequired: updatedServices
        };

        await updateJobCard(updatedJobCard);
    };

    const handleUploadComplete = async (uploadedPhotos) => {
        await addPhotos(uploadedPhotos);
    };

    const handlePhotosUpdate = async (updatedPhotos) => {
        const removedPhoto = photos.find(
            p => !updatedPhotos.find(up => up.storagePath === p.storagePath)
        );

        if (removedPhoto) {
            await removePhoto(removedPhoto);
        }
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <Spinner size="lg" />
            </div>
        );
    }


    if (!jobCard) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <Card className="max-w-md">
                    <div className="p-8 text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                            </svg>
                        </div>
                        <h3 className="mt-4 text-lg font-semibold text-gray-900">Job Card Not Found</h3>
                        <p className="mt-2 text-sm text-gray-600">The job card you're looking for doesn't exist or has been removed.</p>
                        <div className="mt-6">
                            <Link to="/vehicles/alljobs" className="text-primary-600 hover:text-primary-700 font-medium">
                                ← Back to All Jobs
                            </Link>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }


    const renderComponent = () => {
        switch (activeComponent) {
            case 'techdetails':
                return <TechDetails jobCard={jobCard} updateJobCard={updateJobCard} />;
            case 'partsAndServices':
                return <PartsAndServices jobCard={jobCard} handleAddPart={handleAddPart} handleAddService={handleAddService} />;
            case 'assignTask':
                return <AssignTask jobCard={jobCard} employees={employees} />;
            case 'invoiceDetails':
                return <JobCardInvoice jobCard={jobCard}  employees={employees} />;
            case 'photos':
                return (
                    <div className="space-y-6">
                        {/* Legal Protection Notice */}
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-800">
                                        Legal Protection - Required Documentation
                                    </h3>
                                    <div className="mt-2 text-sm text-red-700">
                                        <ul className="list-disc pl-5 space-y-1">
                                            <li><strong>Before Repair:</strong> Document ALL existing damage before work begins</li>
                                            <li><strong>After Repair:</strong> Prove work was completed to standard</li>
                                            <li><strong>Parts:</strong> Show parts that were replaced</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Photo Stage Summary */}
                        {photos.length > 0 && (
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                                    <div className="text-3xl font-bold text-orange-600">
                                        {photos.filter(p => p.category?.includes('Before')).length}
                                    </div>
                                    <div className="text-sm text-orange-800 font-semibold mt-1">
                                        Before Repair
                                    </div>
                                </div>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                                    <div className="text-3xl font-bold text-blue-600">
                                        {photos.filter(p => p.category?.includes('During') || p.category?.includes('Progress') || p.category?.includes('Parts')).length}
                                    </div>
                                    <div className="text-sm text-blue-800 font-semibold mt-1">
                                        During Repair
                                    </div>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                                    <div className="text-3xl font-bold text-green-600">
                                        {photos.filter(p => p.category?.includes('After') || p.category?.includes('Final') || p.category?.includes('Quality')).length}
                                    </div>
                                    <div className="text-sm text-green-800 font-semibold mt-1">
                                        After Repair
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Photo Uploader */}
                        <PhotoUploader
                            entityType="job"
                            entityId={jobCard.id}
                            onUploadComplete={handleUploadComplete}
                            existingPhotos={photos}
                            maxPhotos={50}
                            categories={[
                                'Before Repair - Front',
                                'Before Repair - Rear',
                                'Before Repair - Left',
                                'Before Repair - Right',
                                'Before Repair - Damage',
                                'Before Repair - Interior',
                                'During Repair',
                                'Work in Progress',
                                'Parts Removed',
                                'Parts Replaced',
                                'After Repair - Front',
                                'After Repair - Rear',
                                'After Repair - Left',
                                'After Repair - Right',
                                'After Repair - Interior',
                                'Final Result',
                                'Quality Check',
                                'Other'
                            ]}
                        />

                        {/* Photo Gallery */}
                        {photosLoading ? (
                            <div className="text-center py-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                                <p className="mt-4 text-gray-600">Loading photos...</p>
                            </div>
                        ) : (
                            <PhotoGallery
                                entityType="job"
                                entityId={jobCard.id}
                                photos={photos}
                                onPhotosUpdate={handlePhotosUpdate}
                                readOnly={false}
                            />
                        )}
                    </div>
                );
            default:
                return <TechDetails jobCard={jobCard} />;
        }
    };

    return (
        <>
            <BreadCrumb pages={pages} />
            <ToastContainer />
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <Card className="overflow-hidden">
                    <JobHeader jobCard={jobCard} />
                    <StatusBar jobCard={jobCard} />

                    {/* Navigation Tabs */}
                    <div className="border-b border-gray-200 bg-white">
                        <nav className="flex -mb-px space-x-1 px-6" aria-label="Tabs">
                            {[
                                { id: 'techdetails', label: 'Diagnosis Report', count: null },
                                { id: 'partsAndServices', label: 'Parts & Services', count: null },
                                { id: 'photos', label: 'Photos', count: photos.length },
                                { id: 'assignTask', label: 'Assign Task', count: null },
                                { id: 'invoiceDetails', label: 'Invoices', count: null },
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveComponent(tab.id)}
                                    className={`
                                        whitespace-nowrap py-4 px-4 border-b-2 font-medium text-sm transition-all
                                        ${activeComponent === tab.id
                                            ? 'border-primary-600 text-primary-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }
                                    `}
                                >
                                    <span className="flex items-center space-x-2">
                                        <span>{tab.label}</span>
                                        {tab.count !== null && (
                                            <Badge variant="primary" size="sm">
                                                {tab.count}
                                            </Badge>
                                        )}
                                    </span>
                                </button>
                            ))}
                        </nav>
                    </div>

                    <div className="bg-gray-50 p-6">
                        {renderComponent()}
                    </div>
                </Card>
            </div>
        </>
    );
};

export default JobUpdate;
